import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import { refreshStoreAfter } from '../storeRefresh';

export interface StoreBrandRow {
    id: string;
    name: string;
    supplierSlug: string;
    legalName: string;
    postalAddress: string;
    contactEmail: string;
    euResponsibleName: string;
    euResponsibleAddress: string;
    euResponsibleEmail: string;
    productCount: number;
}

export interface PendingBrandRow {
    productId: number;
    name: string;
    reference: string;
    sourceText: string;
    supplierSlug: string;
    isActive: boolean;
}

export interface SupplierBrandTerm {
    brandId: string;
    brandName: string;
    deliveryDaysMin: number | null;
    deliveryDaysMax: number | null;
}

function text(value: unknown): string {
    return value == null ? '' : String(value);
}

export function useStoreBrands() {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['admin', 'store-brands'],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_store_brands', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const payload = (normalizeRpcResponse(data)[0] ?? {}) as { items?: StoreBrandRow[] };
            return (payload.items ?? []).map((row) => ({
                ...row,
                legalName: text(row.legalName),
                postalAddress: text(row.postalAddress),
                contactEmail: text(row.contactEmail),
                euResponsibleName: text(row.euResponsibleName),
                euResponsibleAddress: text(row.euResponsibleAddress),
                euResponsibleEmail: text(row.euResponsibleEmail),
                productCount: Number(row.productCount) || 0,
            }));
        },
    });
}

export function usePendingStoreBrands() {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['admin', 'store-brands-pending'],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_store_brand_pending', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const payload = (normalizeRpcResponse(data)[0] ?? {}) as { items?: PendingBrandRow[] };
            return (payload.items ?? []).map((row) => ({
                ...row,
                productId: Number(row.productId),
                sourceText: text(row.sourceText),
                isActive: row.isActive !== false,
            }));
        },
    });
}

export function useUpdateStoreBrand() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (row: StoreBrandRow) => {
            const { error } = await dbClient.rpc('admin_update_store_brand', {
                p_admin_user_id: user!.id,
                p_brand_id: row.id,
                p_legal_name: row.legalName,
                p_postal_address: row.postalAddress,
                p_contact_email: row.contactEmail,
                p_eu_responsible_name: row.euResponsibleName,
                p_eu_responsible_address: row.euResponsibleAddress,
                p_eu_responsible_email: row.euResponsibleEmail,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => refreshStoreAfter(qc, 'brand-save'),
    });
}

export function useSetProductBrand() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { productId: number; brand: string; compatible?: string | null }) => {
            const { error } = await dbClient.rpc('admin_set_catalog_product_brand', {
                p_admin_user_id: user!.id,
                p_product_id: input.productId,
                p_brand: input.brand,
                p_compatible: input.compatible ?? null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => refreshStoreAfter(qc, 'brand-assign'),
    });
}

export function useSupplierBrandTerms(slug: string) {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['admin', 'store-brand-terms', slug],
        enabled: Boolean(user?.id && slug),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_supplier_brand_terms', {
                p_admin_user_id: user!.id,
                p_supplier_slug: slug,
            });
            if (error) throw new Error(error.message);
            const payload = (normalizeRpcResponse(data)[0] ?? {}) as { items?: SupplierBrandTerm[] };
            return (payload.items ?? []).map((row) => ({
                ...row,
                deliveryDaysMin: row.deliveryDaysMin == null ? null : Number(row.deliveryDaysMin),
                deliveryDaysMax: row.deliveryDaysMax == null ? null : Number(row.deliveryDaysMax),
            }));
        },
    });
}

export function useUpsertBrandTerm(slug: string) {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { brandId: string; daysMin: number | null; daysMax: number | null }) => {
            const { error } = await dbClient.rpc('admin_upsert_brand_supplier_term', {
                p_admin_user_id: user!.id,
                p_brand_id: input.brandId,
                p_supplier_slug: slug,
                p_days_min: input.daysMin,
                p_days_max: input.daysMax,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store-brand-terms', slug] });
        },
    });
}

export function useMerchPrepDays() {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['admin', 'store-merch-prep'],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_store_fulfillment', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const row = (normalizeRpcResponse(data)[0] ?? {}) as { merchPrepDays?: number };
            return Number(row.merchPrepDays) || 0;
        },
    });
}

export function useSetMerchPrepDays() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (days: number) => {
            const { error } = await dbClient.rpc('admin_set_merch_prep_days', {
                p_admin_user_id: user!.id,
                p_days: days,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store-merch-prep'] });
        },
    });
}
